import React, { useState, useCallback } from 'react';
import { ClipboardList, ArrowRight, Trophy } from 'lucide-react';
import { questions, categories } from './data/questions';
import { QuestionnaireSection } from './components/QuestionnaireSection';
import { ProgressBar } from './components/ProgressBar';
import { CareerSuggestions } from './components/CareerSuggestions';
import { Answer, CareerSuggestion } from './types';
import { analyzeAnswers } from './services/careerMatcher';

function App() {
  const [currentCategory, setCurrentCategory] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [suggestions, setSuggestions] = useState<CareerSuggestion[]>([]);

  const handleAnswerChange = useCallback((questionId: string, response: string) => {
    setAnswers(prev => {
      const existing = prev.find(a => a.questionId === questionId);
      if (existing) {
        return prev.map(a => a.questionId === questionId ? { ...a, response } : a);
      }
      return [...prev, { questionId, response }];
    });
  }, []);

  const currentQuestions = questions.filter(q => 
    q.category === categories[currentCategory].id
  );

  const handleNext = () => {
    if (currentCategory < categories.length - 1) {
      setCurrentCategory(prev => prev + 1);
    } else {
      const careerSuggestions = analyzeAnswers(answers);
      setShowResults(true);
      setSuggestions(careerSuggestions);
    }
  };

  const canProceed = currentQuestions.every(q => 
    answers.some(a => a.questionId === q.id && a.response.trim() !== '')
  );

  if (showResults) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <CareerSuggestions suggestions={suggestions} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <ClipboardList className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              Career Explorer
            </h1>
          </div>
          <p className="text-gray-600">
            Discover your ideal career path through self-reflection
          </p>
        </div>

        <ProgressBar 
          current={currentCategory + 1} 
          total={categories.length} 
        />

        <div className="bg-white shadow-lg rounded-lg p-6 relative">
          <div className="border-b pb-4 mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {categories[currentCategory].title}
            </h2>
            <p className="text-gray-600">
              {categories[currentCategory].description}
            </p>
          </div>

          <div className="space-y-6 mb-6">
            <QuestionnaireSection
              category={categories[currentCategory].id}
              questions={currentQuestions}
              answers={answers}
              onAnswerChange={handleAnswerChange}
            />
          </div>

          <div className="sticky bottom-0 pt-4 bg-white z-10 border-t border-gray-100">
            <div className="flex justify-between items-center">
              {currentCategory > 0 && (
                <button
                  onClick={() => setCurrentCategory(prev => prev - 1)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Back
                </button>
              )}
              <div className={currentCategory === 0 ? 'ml-auto' : ''}>
                <button
                  onClick={handleNext}
                  disabled={!canProceed}
                  className={`relative inline-flex items-center gap-2 px-6 py-2 rounded-md transition-colors ${
                    canProceed 
                      ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  style={{ zIndex: 20 }}
                >
                  <span className="relative z-10">
                    {currentCategory < categories.length - 1 ? 'Next' : 'Get Results'}
                  </span>
                  <ArrowRight className="w-4 h-4 relative z-10" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;